import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { ConfigureSubscription } from '@/sections/settings/subscription/configure';
import { SubscriptionSummary } from '@/sections/settings/subscription/summary';
import { SubscriptionTiers } from '@/sections/settings/subscription/tier';
import { useAuth } from '@/state/auth';
import { MonitoringCounts, SubscriptionConfiguration } from '@/types';

/**
 * Subscription component displays subscription details and configuration.
 * Data fetching is now handled by parent (Settings page) to prevent layout shift.
 *
 * This component receives pre-fetched subscription and monitoring data as props,
 * eliminating the need for internal data fetching and loading states.
 */
interface SubscriptionProps {
  subscriptionValue?: SubscriptionConfiguration;
  monitoringCounts?: MonitoringCounts;
}

export function Subscription({
  subscriptionValue,
  monitoringCounts,
}: SubscriptionProps) {
  const { isPraetorianUser, isImpersonating, friend } = useAuth();
  const [configureSubscriptionModalOpen, setConfigureSubscriptionModalOpen] =
    useState(false);

  return (
    <div className="relative flex h-full flex-col gap-6">
      {subscriptionValue && (
        <>
          {isPraetorianUser && (
            <Button
              className="absolute top-0 right-0"
              size="sm"
              onClick={() => {
                setConfigureSubscriptionModalOpen(true);
              }}
            >Update Subscription</Button>
          )}
          <div>
            <h2 className="text-chariot-brand-primary mb-6 text-2xl font-bold">
              Current Subscription
            </h2>
            <SubscriptionSummary
              {...subscriptionValue}
              pricingCurrency="USD"
              billingCycle="annual"
              billingPaymentMethod="Invoice"
              monitoringCounts={monitoringCounts}
            />
          </div>
          <div className="mt-6">
            <h2 className="text-chariot-brand-primary mb-6 text-2xl font-bold">
              Available Subscription Tiers
            </h2>
            <div className="overflow-visible pt-4">
              <SubscriptionTiers
                selectedTier={subscriptionValue.selectedTier}
              />
            </div>
          </div>
        </>
      )}
      {!subscriptionValue && isPraetorianUser && (
        <div className="flex h-full items-center justify-center">
          <Button
            size="sm"
            onClick={() => {
              setConfigureSubscriptionModalOpen(true);
            }}
          >Configure Subscription</Button>
        </div>
      )}
      <ConfigureSubscription
        open={configureSubscriptionModalOpen}
        onClose={() => setConfigureSubscriptionModalOpen(false)}
        customerEmail={isImpersonating ? friend : undefined}
      />
    </div>
  );
}
